import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Video, MessageCircle, X, Reply, Calendar, UserPlus } from 'lucide-react';
import { useSocket } from '../../contexts/SocketContext';
import { getCurrentUser } from '../../utils/auth';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  playClassNotificationSound,
  playChatNotificationSound,
  playEnquiryNotificationSound,
} from '../../utils/notificationSound';
import { routeNotification } from '../../utils/notificationManager';
import './NotificationToast.scss';

const TOAST_DURATION = 8000;
const CHAT_TOAST_DURATION = 6000;
const MAX_TOASTS = 3;

function getInitials(name) {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return parts[0][0]?.toUpperCase() || '?';
}

const NotificationToast = () => {
  const { socket } = useSocket();
  const [toasts, setToasts] = useState([]);
  const toastIdRef = useRef(0);
  const location = useLocation();
  const user = getCurrentUser();
  const navigate = useNavigate();

  const removeToast = useCallback((id) => {
    setToasts(prev => prev.map(t => t.id === id ? { ...t, exiting: true } : t));
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, 300);
  }, []);

  const addToast = useCallback((toast) => {
    const id = ++toastIdRef.current;
    const newToast = { ...toast, id, exiting: false, createdAt: Date.now() };

    setToasts(prev => {
      const updated = [...prev, newToast];
      if (updated.length > MAX_TOASTS) {
        return updated.slice(updated.length - MAX_TOASTS);
      }
      return updated;
    });

    const duration = toast.type === 'class_started' ? TOAST_DURATION : CHAT_TOAST_DURATION;
    setTimeout(() => removeToast(id), duration);

    return id;
  }, [removeToast]);

  // ── Listen for class_started notifications ──
  useEffect(() => {
    if (!socket) return;

    const handleNotification = (data) => {
      if (data.type === 'class_started') {
        const route = routeNotification({
          type: 'class_started',
          tag: `class-${data.batchId}`,
          batchId: data.batchId,
        });

        if (!route.show) return;

        // In-app: show toast + play sound
        if (route.method === 'in-app') {
          playClassNotificationSound();
          addToast({
            type: 'class_started',
            title: data.batchName || data.title || 'Class Started',
            message: data.message || 'The meeting has started',
            batchId: data.batchId,
          });
        }
        // Background: SW handles native push; just play sound if tab is still alive
      }
    };

    socket.on('notification', handleNotification);
    return () => socket.off('notification', handleNotification);
  }, [socket, addToast, navigate, user?.usertype]);

  // ── Listen for chat messages when NOT on the chat page ──
  useEffect(() => {
    if (!socket) return;

    const handleChatNotification = (data) => {
      const currentUserId = user?._id || user?.id;
      if (data.sender === currentUserId) return;

      const isOnChatPage = location.pathname.includes('/chat');
      if (isOnChatPage) return;

      const batchId = data.batch?._id || data.batch;
      const route = routeNotification({
        type: 'chat_message',
        tag: `chat-${batchId}`,
        batchId,
      });

      if (!route.show) return;

      if (route.method === 'in-app') {
        playChatNotificationSound();

        const msgPreview = data.message
          ? (data.message.length > 60 ? data.message.substring(0, 60) + '...' : data.message)
          : 'Sent an attachment';

        addToast({
          type: 'chat_message',
          title: data.senderName || 'New Message',
          message: msgPreview,
          senderName: data.senderName,
          batchName: data.batchName,
          batchId,
        });
      }
      // Background: SW handles native push
    };

    socket.on('chat:notification', handleChatNotification);
    return () => socket.off('chat:notification', handleChatNotification);
  }, [socket, addToast, navigate, location.pathname, user]);

  // ── Listen for new enquiry notifications (from Zoho / public forms) ──
  useEffect(() => {
    if (!socket) return;

    const handleNotification = (data) => {
      if (data.type !== 'new_enquiry') return;

      // Only show to admins
      const ut = user?.usertype;
      if (!['admin', 'superadmin'].includes(ut)) return;

      // Don't show if already on enquiries page
      if (location.pathname.includes('/enquiries')) return;

      playEnquiryNotificationSound();
      addToast({
        type: 'new_enquiry',
        title: data.title || 'New Enquiry',
        message: data.message || data.fullName || 'A new lead has arrived',
        link: data.link,
        enquiryId: data.enquiryId,
        source: data.source,
      });
    };

    socket.on('notification', handleNotification);
    return () => socket.off('notification', handleNotification);
  }, [socket, addToast, location.pathname, user]);

  const handleJoinClass = (toast) => {
    removeToast(toast.id);
    const usertype = user?.usertype;
    if (usertype === 'student') {
      navigate(`/s/classroom/${toast.batchId}`);
    } else if (usertype === 'instructor') {
      navigate(`/i/classroom/${toast.batchId}`);
    }
  };

  const handleGoToChat = (toast) => {
    removeToast(toast.id);
    const prefix = user?.usertype === 'student' ? '/s' : user?.usertype === 'instructor' ? '/i' : '';
    navigate(`${prefix}/chat`);
  };

  const handleGoToEnquiry = (toast) => {
    removeToast(toast.id);
    if (toast.link) {
      navigate(toast.link);
    } else {
      navigate('/enquiries');
    }
  };

  if (toasts.length === 0) return null;

  return (
    <div className="teams-toast-container">
      {toasts.map((toast) => (
        <div
          key={toast.id}
          className={`teams-toast teams-toast--${toast.type} ${toast.exiting ? 'teams-toast--exit' : 'teams-toast--enter'}`}
        >
          {/* Close button */}
          <button
            className="teams-toast__close"
            onClick={() => removeToast(toast.id)}
            aria-label="Dismiss"
          >
            <X size={14} />
          </button>

          {toast.type === 'class_started' ? (
            /* ── Meeting Started (Centered card layout like Teams screenshot 1) ── */
            <>
              <div className="teams-toast__meeting-header" />
              <div className="teams-toast__meeting-body">
                <div className="teams-toast__meeting-icon">
                  <Calendar size={22} />
                </div>
                <div className="teams-toast__meeting-title">{toast.title}</div>
                <div className="teams-toast__meeting-subtitle">{toast.message}</div>
              </div>
              <div className="teams-toast__meeting-actions">
                <button
                  className="teams-toast__action-btn teams-toast__action-btn--join"
                  onClick={() => handleJoinClass(toast)}
                >
                  <Video size={16} />
                  <span>Join</span>
                </button>
                <button
                  className="teams-toast__action-btn teams-toast__action-btn--message"
                  onClick={() => handleGoToChat(toast)}
                >
                  <MessageCircle size={16} />
                  <span>Message</span>
                </button>
              </div>
            </>
          ) : toast.type === 'new_enquiry' ? (
            /* ── New Enquiry / Zoho Lead ── */
            <>
              <div className="teams-toast__chat-body">
                <div className="teams-toast__chat-avatar teams-toast__chat-avatar--enquiry">
                  <UserPlus size={18} />
                </div>
                <div className="teams-toast__chat-content">
                  <div className="teams-toast__chat-sender">{toast.title}</div>
                  <div className="teams-toast__chat-message">{toast.message}</div>
                  {toast.source && (
                    <div className="teams-toast__chat-message" style={{ fontSize: '0.7rem', opacity: 0.7 }}>
                      Source: {toast.source}
                    </div>
                  )}
                </div>
              </div>
              <div className="teams-toast__chat-actions">
                <button
                  className="teams-toast__chat-reply-btn"
                  onClick={() => handleGoToEnquiry(toast)}
                >
                  <UserPlus size={16} />
                  <span>View Enquiry</span>
                </button>
              </div>
            </>
          ) : (
            /* ── Chat Message (Horizontal layout like Teams screenshot 2) ── */
            <>
              <div className="teams-toast__chat-body">
                <div className="teams-toast__chat-avatar">
                  <span>{getInitials(toast.senderName)}</span>
                </div>
                <div className="teams-toast__chat-content">
                  <div className="teams-toast__chat-sender">{toast.title}</div>
                  <div className="teams-toast__chat-message">{toast.message}</div>
                </div>
                <button
                  className="teams-toast__chat-icon-btn"
                  onClick={() => handleGoToChat(toast)}
                  title="Open chat"
                >
                  <MessageCircle size={18} />
                </button>
              </div>
              <div className="teams-toast__chat-actions">
                <button
                  className="teams-toast__chat-reply-btn"
                  onClick={() => handleGoToChat(toast)}
                >
                  <Reply size={16} />
                  <span>Reply</span>
                </button>
              </div>
            </>
          )}

          {/* Progress bar */}
          <div
            className="teams-toast__progress"
            style={{
              animationDuration: `${toast.type === 'class_started' ? TOAST_DURATION : CHAT_TOAST_DURATION}ms`,
            }}
          />
        </div>
      ))}
    </div>
  );
};

export default NotificationToast;
